import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Disclaimer from "../components/Disclaimer";
import { BloodworkBadge } from "../components/Badges";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";

const STARS = { established: 5, moderate: 4, limited: 3, preclinical: 2 };
const LABELS = {
  established: "Large Human Trials",
  moderate: "Multiple Human Studies",
  limited: "Small Human Studies",
  preclinical: "Preclinical",
};

function Stars({ level }) {
  const filled = STARS[level] || 1;
  return (
    <div className="flex items-center gap-1 text-[color:var(--vv-gold)]">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} aria-hidden style={{ opacity: i <= filled ? 1 : 0.25 }}>★</span>
      ))}
      <span className="ml-2 text-[10px] uppercase tracking-widest text-stone-500">{LABELS[level] || "Emerging"}</span>
    </div>
  );
}

const groups = [
  { key: "established", title: "Large Human Trials" },
  { key: "moderate",    title: "Multiple Human Studies" },
  { key: "limited",     title: "Small Human Studies" },
  { key: "preclinical", title: "Primarily Preclinical" },
];

export default function ResearchLibrary() {
  const [therapies, setTherapies] = useState([]);
  useEffect(() => { api.get("/therapies").then(({ data }) => setTherapies(data)); }, []);

  const grouped = groups.map((g) => ({
    ...g,
    items: therapies.filter((t) => t.evidence_level === g.key),
  }));

  return (
    <div data-testid="research-library-page">
      <SEO
        title="Peptide Research Library — Evidence Ratings for Every Therapy"
        description="Peptide therapies grouped by evidence strength: large human trials, multiple human studies, small human studies, or primarily preclinical. Educational content only."
        path="/research"
        breadcrumbs={[{ name: "Research Library", path: "/research" }]}
      />
      <section className="vv-hero">
        <div className="vv-container py-16">
          <Breadcrumbs items={[{ name: "Research Library", path: "/research" }]} />
          <div className="vv-hero-eyebrow">Research Library</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3">Evidence at a glance.</h1>
          <p className="mt-4 max-w-2xl">Each therapy is rated by the strength of clinical evidence — from large human trials down to primarily preclinical research. Ratings are educational; a licensed provider decides what may be appropriate for you.</p>
        </div>
      </section>

      <section className="vv-container py-14 space-y-12">
        {grouped.map((g) => g.items.length === 0 ? null : (
          <div key={g.key}>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-2xl">{g.title}</h2>
              <Stars level={g.key} />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {g.items.map((t) => (
                <Link key={t.slug} to={`/peptide-library/${t.slug}`} data-testid={`research-item-${t.slug}`} className="vv-card p-5 block">
                  <div className="font-display text-xl">{t.public_name}</div>
                  <div className="text-xs text-stone-500 mt-1">{t.clinical_name}</div>
                  <p className="text-sm text-stone-600 mt-3 line-clamp-3">{t.short_description}</p>
                  <div className="mt-3 flex justify-between items-center">
                    <BloodworkBadge level={t.bloodwork_level} />
                    <Stars level={t.evidence_level} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-6"><Disclaimer /></div>
      </section>
    </div>
  );
}
